import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getCurrentUser } from "aws-amplify/auth";
import { YieldApi } from "@/App/api/yield";
import { getUserHotelsPermissions } from "@/App/auth/auth";
import { getAccessibleHotels } from "@/common/permissionHandler";
import { setHotels } from "@/state/store/slice/hotelSlice";
import { setEmail, setPermissions } from "@/state/store/slice/userSlice";
import type { RootState } from "@/state/store";
import type { HotelMeta } from "@/models/api.models";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "@/lib/toast";
import AppHeader from "@/components/AppHeader";
import { getUserPermissions } from "@/App/auth/GetUserPermissions";

const ProtectedLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const hotels = useSelector(
    (state: RootState) => state.hotelData.hotels,
  ) as HotelMeta[];
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const user = await getCurrentUser();
        if (cancelled) return;
        setUsername(user?.signInDetails?.loginId || user?.username || "");

        try {
          const { email, permissions } = await getUserPermissions();
          dispatch(setEmail(email));
          dispatch(setPermissions(permissions));
        } catch {
          // Non-fatal: permissions only gate the action buttons
        }

        const rawPermissions = await getUserHotelsPermissions();
        const allHotels = await YieldApi.getUsersHotelsMeta();
        const { hotels: accessible } = getAccessibleHotels(
          rawPermissions,
          allHotels ?? [],
        );
        if (cancelled) return;
        dispatch(setHotels(accessible as unknown as Record<string, unknown>[]));

        const urlHotelId = location.pathname.split("/").filter(Boolean)[0];
        if (!urlHotelId && accessible.length > 0) {
          navigate(`/${accessible[0].hotelId}`, { replace: true });
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to initialise session:", error);
          toast.error("Failed to load your hotels.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  return (
    <div className="flex min-h-screen flex-col">
      <AppHeader username={username} />
      <main className="mx-auto w-full max-w-6xl flex-1 px-3 py-4 sm:px-6">
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <Spinner className="h-6 w-6" />
          </div>
        ) : hotels.length === 0 ? (
          <div className="rounded-md border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
            You do not have access to any hotels.
          </div>
        ) : (
          <Outlet />
        )}
      </main>
    </div>
  );
};

export default ProtectedLayout;
